
import { useCallback } from "react";
import { Card } from "@/components/ui/card";
import { InventoryItem } from "@/types/inventory";
import { LoadingState } from "./LoadingState";
import { EmptyState } from "./EmptyState";
import { InventoryItemDetails } from "./InventoryItemDetails";
import { InventoryTimestamp } from "./InventoryTimestamp";
import { sortInventoryResults, generateInventoryItemKey } from "@/utils/inventory/sortUtils";

interface Props {
  results: InventoryItem[];
  isLoading: boolean;
  hasSearched?: boolean;
  lastUpdate?: string | null;
}

export const InventorySearchResults = ({ results, isLoading, hasSearched = false, lastUpdate }: Props) => {
  const renderContent = useCallback(() => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (results.length === 0) {
      return <EmptyState hasSearched={hasSearched} />;
    }

    // Use enhanced sorting that prioritizes location quality
    const sortedResults = sortInventoryResults(results);

    return sortedResults.map((result, index) => {
      const uniqueKey = generateInventoryItemKey(result, index);
      
      return (
        <div
          key={uniqueKey}
          className="p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-start gap-3">
            <InventoryItemDetails item={result} />
          </div>
        </div>
      );
    });
  }, [results, isLoading, hasSearched]);

  return (
    <Card className="mt-2 divide-y">
      <InventoryTimestamp lastUpdate={lastUpdate} />
      {renderContent()}
    </Card>
  );
};
